<template>
  <div class="settings-card deactivation-card" :class="{ 'card-disabled': shouldDisableScheduleCard }">
    <div class="card-header justify-between">
      <div class="flex align-items-center">
        <feather-icon class="card-icon" icon="ClockIcon"/>
        <span class="card-title">Agendamento de Desativação</span>
      </div>

      <div class="inline-flex gap-2" v-if="!shouldDisableScheduleCard">
        <vx-tooltip v-if="hasOpenDeactivationSchedule" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Ver solicitação">
          <feather-icon @click="goToOpenScheduleDetail" icon="EyeIcon"/>
        </vx-tooltip>
        <vx-tooltip v-if="canCreateSchedule" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Criar agendamento">
          <feather-icon @click="openScheduleModal" icon="PlusIcon"/>
        </vx-tooltip>
        <vx-tooltip v-if="canEditOpenSchedule" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Editar agendamento">
          <feather-icon @click="openScheduleModal" icon="Edit3Icon"/>
        </vx-tooltip>
        <vx-tooltip v-if="canCancelOpenSchedule" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Cancelar agendamento">
          <feather-icon @click="openCancelScheduleModal" icon="XIcon"/>
        </vx-tooltip>
        <vx-tooltip class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Atualizar agendamento">
          <feather-icon @click="loadOpenDeactivationSchedule" icon="RefreshCwIcon"/>
        </vx-tooltip>
      </div>
      <span v-else class="disabled-badge">Indisponível</span>
    </div>

    <div class="card-divider"></div>

    <div v-if="showScheduleCard" class="card-content">
      <div v-if="openScheduleLoading" class="disabled-content">
        <p class="disabled-message">Carregando agendamento...</p>
      </div>

      <div v-else-if="hasOpenDeactivationSchedule" class="deactivation-details-grid">
        <div class="info-item">
          <div class="info-label">Agendado para</div>
          <div class="info-value">{{ openScheduleAt }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">Solicitado por</div>
          <div class="info-value">{{ openScheduleRequestedBy }}</div>
        </div>
        <div class="info-item" v-if="openScheduleClickupLink">
          <div class="info-label">ClickUp</div>
          <div class="info-value">
            <a :href="openScheduleClickupLink" target="_blank" rel="noopener noreferrer" class="schedule-link">Abrir solicitação</a>
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">Motivo</div>
          <div class="info-value reason-content">{{ openScheduleReason }}</div>
        </div>
      </div>

      <div v-else class="disabled-content">
        <p class="disabled-message">Nenhum agendamento de desativação aberto para este tenant.</p>
      </div>
    </div>

    <div v-else class="card-content disabled-content">
      <p class="disabled-message">Esta funcionalidade está indisponível.</p>
    </div>

    <RequestFormModal
      :active.sync="scheduleModal.active"
      :request="canEditOpenSchedule ? openDeactivationSchedule : null"
      :fixed-target-id="tenantId"
      :fixed-tenant-label="scheduleTenantLabel"
      @saved="handleScheduleSaved"
    />

    <vs-popup title="Cancelar agendamento" :active.sync="cancelScheduleModal.active" class="cancel-popup">
      <div v-if="hasOpenDeactivationSchedule">
        <p class="mb-2 text-sm text-grey">ID #{{ openDeactivationSchedule.id }} · desativação agendada</p>
        <vs-textarea
          v-model="cancelScheduleModal.comment"
          label="Motivo do cancelamento"
          placeholder="Escreva o motivo do cancelamento"
          class="mb-4"
        />
        <div class="flex justify-end gap-2">
          <vs-button type="border" @click="closeCancelScheduleModal" :disabled="cancelScheduleModal.loading">Voltar</vs-button>
          <vs-button color="danger" @click="confirmCancelSchedule" :loading="cancelScheduleModal.loading">Cancelar agendamento</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { hasPermissionBackOffice } from '@/utils'
import RequestFormModal from '@/components/approvals/RequestFormModal.vue'

export default {
  name: 'TenantDeactivationCard',
  components: {
    RequestFormModal
  },
  props: {
    tenantData: {
      type: Object,
      default: null
    },
    isOriginBackoffice: {
      type: Boolean,
      default: false
    },
    wrongBackoffice: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      scheduleModal: {
        active: false
      },
      cancelScheduleModal: {
        active: false,
        comment: '',
        loading: false
      },
      openDeactivationSchedule: null,
      openScheduleLoading: false
    }
  },
  computed: {
    isTenantDeleted() {
      return !!(this.tenantData && this.tenantData.deleted_at);
    },
    currentUserId() {
      return this.$store.state.AppActiveUser ? this.$store.state.AppActiveUser.id : null;
    },
    tenantId() {
      return this.tenantData && this.tenantData.id ? this.tenantData.id : 0;
    },
    shouldDisableScheduleCard() {
      return !this.tenantData || this.isTenantDeleted || this.wrongBackoffice || !this.isOriginBackoffice;
    },
    showScheduleCard() {
      return !!(this.tenantData && !this.tenantData.deleted_at && this.isOriginBackoffice && !this.wrongBackoffice);
    },
    hasOpenDeactivationSchedule() {
      return !!(this.openDeactivationSchedule && this.openDeactivationSchedule.id);
    },
    canCreateSchedule() {
      return this.showScheduleCard && !this.hasOpenDeactivationSchedule && hasPermissionBackOffice('requests');
    },
    canEditOpenSchedule() {
      return this.hasOpenDeactivationSchedule && hasPermissionBackOffice('requests-update');
    },
    canCancelOpenSchedule() {
      return this.hasOpenDeactivationSchedule && hasPermissionBackOffice('requests-remove');
    },
    scheduleTenantLabel() {
      if (!this.tenantData) { return ''}
      const tenantAlias = this.tenantData.fqdn ? this.tenantData.fqdn.split('.')[0] : `ID ${this.tenantData.id}`
      return `${tenantAlias} (ID ${this.tenantData.id})`
    },
    openScheduleAt() {
      if (!this.hasOpenDeactivationSchedule) { return 'Não informado' }
      const scheduledAt = this.openDeactivationSchedule.scheduled_at || (this.openDeactivationSchedule.scheduled_action ? this.openDeactivationSchedule.scheduled_action.executes_at : null)
      return scheduledAt ? new Date(scheduledAt).toLocaleString('pt-BR') : 'Não informado'
    },
    openScheduleRequestedBy() {
      if (!this.hasOpenDeactivationSchedule) { return 'Não informado' }
      return this.openDeactivationSchedule.requester && this.openDeactivationSchedule.requester.name ? this.openDeactivationSchedule.requester.name : 'Não informado'
    },
    openScheduleReason() {
      if (!this.hasOpenDeactivationSchedule) { return 'Não informado' }
      return this.openDeactivationSchedule.payload && this.openDeactivationSchedule.payload.motivo ? this.openDeactivationSchedule.payload.motivo : 'Não informado'
    },
    openScheduleClickupLink() {
      if (!this.hasOpenDeactivationSchedule) { return null }
      return this.openDeactivationSchedule.clickup_link || null
    }
  },
  watch: {
    tenantData: {
      immediate: true,
      handler() {
        this.syncScheduleState()
      }
    },
    wrongBackoffice() {
      this.syncScheduleState()
    },
    isOriginBackoffice() {
      this.syncScheduleState()
    }
  },
  methods: {
    syncScheduleState() {
      if (!this.showScheduleCard) {
        this.openDeactivationSchedule = null
        this.openScheduleLoading = false
        return
      }

      this.loadOpenDeactivationSchedule()
    },
    openScheduleModal() {
      if (!this.showScheduleCard) {
        return
      }

      this.scheduleModal.active = true
    },
    async handleScheduleSaved() {
      await this.loadOpenDeactivationSchedule()
      await this.$store.dispatch('approvals/fetchRequests').catch(() => null)
    },
    openCancelScheduleModal() {
      if (!this.canCancelOpenSchedule) {
        return
      }

      this.cancelScheduleModal.active = true
      this.cancelScheduleModal.comment = ''
    },
    closeCancelScheduleModal() {
      this.cancelScheduleModal.active = false
      this.cancelScheduleModal.comment = ''
      this.cancelScheduleModal.loading = false
    },
    async confirmCancelSchedule() {
      if (!this.hasOpenDeactivationSchedule) {
        return
      }

      const comment = (this.cancelScheduleModal.comment || '').trim()

      if (!comment) {
        this.$vs.notify({
          color: 'warning',
          title: 'Campos obrigatórios',
          text: 'Informe o motivo do cancelamento antes de continuar.'
        })
        return
      }

      this.cancelScheduleModal.loading = true

      try {
        await this.$store.dispatch('approvals/cancelRequest', {
          id: this.openDeactivationSchedule.id,
          comment
        })

        await this.handleScheduleSaved()
        this.$vs.notify({
          color: 'success',
          title: 'Agendamento',
          text: 'Agendamento cancelado.'
        })
        this.closeCancelScheduleModal()
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: 'Não foi possível cancelar o agendamento.'
        })
      } finally {
        this.cancelScheduleModal.loading = false
      }
    },
    async loadOpenDeactivationSchedule() {
      if (!this.showScheduleCard || !this.tenantId) {
        this.openDeactivationSchedule = null
        this.openScheduleLoading = false
        return null
      }

      this.openScheduleLoading = true

      try {
        const schedule = await this.$store.dispatch('approvals/fetchOpenScheduleByTenant', this.tenantId)
        this.openDeactivationSchedule = schedule
        return schedule
      } catch (exception) {
        console.error(exception)
        this.openDeactivationSchedule = null
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível carregar o agendamento de desativação.'
        })
        return null
      } finally {
        this.openScheduleLoading = false
      }
    },
    goToOpenScheduleDetail() {
      if (!this.hasOpenDeactivationSchedule) {
        return
      }

      this.$store.dispatch('approvals/setCurrentRequest', this.openDeactivationSchedule)
      this.$router.push(`/approvals/${this.openDeactivationSchedule.id}`)
    }
  }
}
</script>

<style scoped lang="scss">
.card-header {
  display: flex;
  padding: 1rem;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}

.align-items-center {
  align-items: center !important;
}

.card-icon {
  width: 16px;
  height: 16px;
  color: #666;
  margin-right: 0.5rem;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.card-divider {
  height: 1px;
  background-color: #e8e8e8;
}

.card-content {
  flex: 1;
  padding: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 11px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.disabled-badge {
  margin-left: auto;
  font-size: 11px;
  color: #f44336;
  font-weight: 600;
  background-color: #ffebee;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.disabled-content {
  text-align: center;
  padding: 2rem 1rem;
}

.disabled-message {
  color: #666;
  font-size: 13px;
  margin: 0;
  font-style: italic;
}

.deactivation-details-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.reason-content {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.schedule-link {
  color: #7367f0;
}
</style>
